import React, { useEffect, useMemo, useRef, useState } from "react";

const levenshtein = (a, b) => {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    let prev = new Array(b.length + 1);
    let curr = new Array(b.length + 1);
    for (let j = 0; j <= b.length; j += 1) prev[j] = j;

    for (let i = 1; i <= a.length; i += 1) {
        curr[0] = i;
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(
                curr[j - 1] + 1,
                prev[j] + 1,
                prev[j - 1] + cost
            );
        }
        [prev, curr] = [curr, prev];
    }
    return prev[b.length];
};

const tokenSimilarity = (a, b) => {
    if (a === b) return 1;
    if (!a.length || !b.length) return 0;
    const maxLen = Math.max(a.length, b.length);
    return 1 - levenshtein(a, b) / maxLen;
};

const fuzzyScore = (label, query) => {
    const lowerLabel = String(label).toLowerCase();
    const lowerQuery = query.toLowerCase().trim();
    if (!lowerQuery) return 1;

    if (lowerLabel.includes(lowerQuery)) {
        return lowerLabel.startsWith(lowerQuery) ? 3 : 2.5;
    }

    const queryTokens = lowerQuery.split(/\s+/).filter(Boolean);
    const labelTokens = lowerLabel.split(/\s+/).filter(Boolean);
    if (!queryTokens.length || !labelTokens.length) return 0;

    let total = 0;
    for (const qt of queryTokens) {
        let best = 0;
        for (const lt of labelTokens) {
            if (lt === qt) { best = 1; break; }
            if (lt.startsWith(qt)) { best = Math.max(best, 0.95); continue; }
            if (lt.includes(qt)) { best = Math.max(best, 0.85); continue; }
            const sim = tokenSimilarity(qt, lt);
            if (sim > best) best = sim;
        }
        total += best;
    }
    return total / queryTokens.length;
};

const SelectWithSearch = ({
    name,
    value,
    options = [],
    onChange = () => { },
    placeholder = "Select an option",
    searchPlaceholder = "Search...",
    emptyMessage = "No results found",
    className = "",
    fuzzyThreshold = 0.5,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selectedOption = useMemo(
        () => options.find((option) => option.value === value),
        [options, value]
    );

    const filteredOptions = useMemo(() => {
        const term = search.trim();
        if (!term) return options;

        const scored = [];
        for (let i = 0; i < options.length; i += 1) {
            const option = options[i];
            const score = fuzzyScore(option.label, term);
            if (score >= fuzzyThreshold) {
                scored.push({ option, index: i, score });
            }
        }

        scored.sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.index - b.index;
        });

        return scored.map(({ option }) => option);
    }, [options, search, fuzzyThreshold]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        setHighlightedIndex(0);
    }, [search, isOpen]);

    useEffect(() => {
        if (!isOpen || !listRef.current) return;
        const node = listRef.current.querySelector(
            `[data-index="${highlightedIndex}"]`
        );
        if (node) {
            node.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex, isOpen]);

    const commitSelection = (option) => {
        onChange({ target: { name, value: option.value } });
        setIsOpen(false);
        setSearch("");
    };

    const handleKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlightedIndex((prev) =>
                Math.min(prev + 1, filteredOptions.length - 1)
            );
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        } else if (event.key === "Enter") {
            event.preventDefault();
            const option = filteredOptions[highlightedIndex];
            if (option) commitSelection(option);
        } else if (event.key === "Escape") {
            event.preventDefault();
            setIsOpen(false);
            setSearch("");
        }
    };

    return (
        <div
            ref={wrapperRef}
            className={`select-with-search ${isOpen ? "is-open" : ""} ${className}`}
        >
            <button
                type="button"
                className="select-with-search__toggle form-input"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span
                    className={`select-with-search__value ${selectedOption ? "" : "select-with-search__value--placeholder"
                        }`}
                >
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <svg
                    className={`select-with-search__arrow ${isOpen ? "is-open" : ""}`}
                    width="12"
                    height="7"
                    viewBox="0 0 14 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        d="M0.699219 0.699219L6.73818 5.69922L12.7771 0.699219"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {isOpen && (
                <div className="select-with-search__panel">
                    <input
                        ref={inputRef}
                        type="text"
                        className="form-input select-with-search__search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={searchPlaceholder}
                    />
                    <ul
                        ref={listRef}
                        className="select-with-search__options"
                        role="listbox"
                    >
                        {filteredOptions.length === 0 ? (
                            <li className="select-with-search__empty">{emptyMessage}</li>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const isSelected = option.value === value;
                                const isHighlighted = index === highlightedIndex;
                                return (
                                    <li
                                        key={option.value}
                                        data-index={index}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`select-with-search__option ${isSelected ? "is-selected" : ""
                                            } ${isHighlighted ? "is-highlighted" : ""}`}
                                        onClick={() => commitSelection(option)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        {option.label}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SelectWithSearch;
